import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Switch from '@mui/material/Switch';
import Slide from '@mui/material/Slide';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { BRAND, PALETTE, ACCESS_KEYS, PERM_KEYS } from './branchTeamsConfig';
import {
  formatCell,
  formatDate,
  getTeamInfo,
  getTeamRoleAccess,
  routeAccessLabel,
} from './branchTeamsUtils';

const ROUTE_PERM_CHECKBOX_UNCHECKED = (
  <Box
    component='span'
    sx={{
      width: 18,
      height: 18,
      display: 'block',
      bgcolor: '#FFFFFF',
      border: '1px solid #000000',
      borderRadius: '2px',
      boxSizing: 'border-box',
    }}
  />
);

const ROUTE_PERM_CHECKBOX_CHECKED = (
  <Box
    component='span'
    sx={{
      width: 18,
      height: 18,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: BRAND.dark,
      border: `1px solid ${BRAND.dark}`,
      borderRadius: '2px',
      boxSizing: 'border-box',
    }}
  >
    <CheckIcon sx={{ fontSize: 14, color: '#FFFFFF' }} />
  </Box>
);

/* ─────────────────────────────────────────────
   Dialog slide transition (unchanged)
───────────────────────────────────────────── */
export const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction='up' ref={ref} {...props} />;
});

/* ─────────────────────────────────────────────
   AccountStatusChip
───────────────────────────────────────────── */
export function AccountStatusChip({ status, reasonStatus }) {
  let s = String(status ?? '').toLowerCase();
  if (typeof status === 'boolean') {
    if (status === true) s = 'active';
    else {
      const rs = String(reasonStatus || '').toLowerCase();
      s = rs.includes('pending') ? 'pending' : 'inactive';
    }
  }
  const map = {
    active: { color: '#16a34a', bg: '#F0FDF4', border: '#86efac', label: 'Active' },
    inactive: { color: '#b91c1c', bg: '#FFF1F0', border: '#fca5a5', label: 'Inactive' },
    pending: { color: '#b45309', bg: '#FFFBEB', border: '#FCD34D', label: 'Pending' },
    suspended: { color: '#7c3aed', bg: '#F5F3FF', border: '#c4b5fd', label: 'Suspended' },
  };
  const cfg = map[s] || {
    color: PALETTE.textSecondary,
    bg: PALETTE.tableHeadBg,
    border: PALETTE.border,
    label: formatCell(status),
  };
  return (
    <Chip
      label={cfg.label}
      size='small'
      sx={{
        fontFamily: 'Inter Bold',
        fontSize: '0.7rem',
        height: 22,
        color: cfg.color,
        bgcolor: cfg.bg,
        border: `1px solid ${cfg.border}`,
        borderRadius: '5px',
      }}
    />
  );
}

/* ─────────────────────────────────────────────
   VerifiedChip
───────────────────────────────────────────── */
export function VerifiedChip({ value }) {
  const isVerified =
    value === true || ['true', 'yes', 'verified', '1'].includes(String(value ?? '').toLowerCase());

  return (
    <Chip
      label={isVerified ? 'Verified' : 'Unverified'}
      size='small'
      sx={{
        fontFamily: 'Inter Bold',
        fontSize: '0.7rem',
        height: 22,
        color: isVerified ? '#0369a1' : PALETTE.textMuted,
        bgcolor: isVerified ? '#F0F9FF' : PALETTE.tableHeadBg,
        border: `1px solid ${isVerified ? '#BAE6FD' : PALETTE.border}`,
        borderRadius: '5px',
      }}
    />
  );
}

/* ─────────────────────────────────────────────
   PermDot — read/write/edit/delete indicator
───────────────────────────────────────────── */
function PermDot({ on }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.45,
        px: 0.9,
        py: 0.25,
        borderRadius: '5px',
        bgcolor: on ? '#F0FDF4' : PALETTE.tableHeadBg,
        border: `1px solid ${on ? '#86efac' : PALETTE.border}`,
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '5px',
          bgcolor: on ? '#16a34a' : PALETTE.border,
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontFamily: 'Inter Bold',
          fontSize: '0.65rem',
          color: on ? '#16a34a' : PALETTE.textMuted,
        }}
      >
        {on ? 'Yes' : 'No'}
      </Typography>
    </Box>
  );
}

/* ─────────────────────────────────────────────
   SectionLabel — section heading inside detail panels
───────────────────────────────────────────── */
function SectionLabel({ icon: Icon, label }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.9,
        mb: 1.5,
        pb: 0.85,
        borderBottom: `1.5px solid ${PALETTE.border}`,
      }}
    >
      {Icon && <Icon sx={{ fontSize: 15, color: BRAND.blue }} />}
      <Typography
        sx={{
          fontFamily: 'Inter Bold',
          fontSize: '0.69rem',
          color: PALETTE.textSecondary,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

/* ─────────────────────────────────────────────
   DetailField — labelled value pair
───────────────────────────────────────────── */
function DetailField({ label, value, mono }) {
  const display = value == null || value === '' ? '—' : String(value);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography
        sx={{
          fontFamily: 'Inter Bold',
          fontSize: '0.66rem',
          color: PALETTE.textMuted,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.8rem',
          color: display === '—' ? PALETTE.border : PALETTE.textPrimary,
          fontFamily: mono ? 'monospace' : 'Inter Regular',
          wordBreak: 'break-all',
        }}
      >
        {display}
      </Typography>
    </Box>
  );
}

/* ─────────────────────────────────────────────
   TeamRowDetailsPanel — expandable row body
───────────────────────────────────────────── */
export function TeamRowDetailsPanel({ row }) {
  const info = getTeamInfo(row);
  const role = getTeamRoleAccess(row);
  const routes = Array.isArray(role.routeAccess)
    ? role.routeAccess
    : Array.isArray(row?.routeAccess)
      ? row.routeAccess
      : [];
  const specials = Array.isArray(role.special_condition_access)
    ? role.special_condition_access
    : Array.isArray(row?.special_condition_access)
      ? row.special_condition_access
      : [];

  return (
    <Box
      sx={{
        bgcolor: PALETTE.tableHeadBg,
        borderTop: `1.5px solid ${PALETTE.border}`,
        px: { xs: 2, sm: 3 },
        py: 3,
      }}
    >
      {/* top two-col grid: team info + role access */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2.5,
          mb: 2.5,
        }}
      >
        {/* ── team info card ── */}
        <Paper
          variant='outlined'
          sx={{
            borderRadius: '5px',
            p: 2.25,
            bgcolor: PALETTE.cardBg,
            border: `1px solid ${PALETTE.border}`,
            boxShadow: '0 2px 8px rgba(44,26,14,0.05)',
          }}
        >
          <SectionLabel icon={AssignmentIndOutlinedIcon} label='Team info' />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {/* <DetailField label='ID' value={info._id} mono /> */}
            <DetailField label='Branch' value={info.branchName} />
            <DetailField label='Email address' value={info.email} />
            <DetailField label='Phone no' value={info.phone_no} />
            <DetailField
              label='Temporary password'
              value={String(info.temporary_password ?? '—')}
            />
            <DetailField label='Reason status' value={info.reason_status} />
            <Box sx={{ gridColumn: '1 / -1' }}>
              {/* <DetailField label='Notes' value={info.manual_notes} /> */}
            </Box>
          </Box>
        </Paper>

        {/* ── role access card ── */}
        <Paper
          variant='outlined'
          sx={{
            borderRadius: '5px',
            p: 2.25,
            bgcolor: PALETTE.cardBg,
            border: `1px solid ${PALETTE.border}`,
            boxShadow: '0 2px 8px rgba(44,26,14,0.05)',
          }}
        >
          <SectionLabel icon={VerifiedUserOutlinedIcon} label='Role access' />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {/* <DetailField label='ID' value={role._id} mono /> */}
            <DetailField label='Dedicated role' value={role.dedicatedRole} />
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Inter Bold',
                  fontSize: '0.66rem',
                  color: PALETTE.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  mb: 0.6,
                }}
              >
                Record active
              </Typography>
              <Chip
                label={role.is_active === true || role.is_active === 'true' ? 'Active' : 'Inactive'}
                size='small'
                sx={{
                  fontFamily: 'Inter Bold',
                  fontSize: '0.68rem',
                  height: 20,
                  bgcolor:
                    role.is_active === true || role.is_active === 'true' ? '#F0FDF4' : '#FFF1F0',
                  color:
                    role.is_active === true || role.is_active === 'true' ? '#16a34a' : '#b91c1c',
                  border: `1px solid ${role.is_active === true || role.is_active === 'true' ? '#86efac' : '#fca5a5'}`,
                  borderRadius: '5px',
                }}
              />
            </Box>
            <DetailField label='Notes' value={role.manual_notes} />
            <DetailField label='Created' value={formatDate(role.created_at)} />
            <DetailField label='Updated' value={formatDate(role.updated_at)} />
          </Box>
        </Paper>
      </Box>

      {/* route access + special conditions */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2.5,
        }}
      >
      {/* ── route access card ── */}
      <Paper
        variant='outlined'
        sx={{
          borderRadius: '5px',
          p: 2.25,
          bgcolor: PALETTE.cardBg,
          border: `1px solid ${PALETTE.border}`,
          boxShadow: '0 2px 8px rgba(44,26,14,0.05)',
        }}
      >
        <SectionLabel icon={VpnKeyOutlinedIcon} label='Route access' />
        {routes.length === 0 ? (
          <Typography sx={{ fontSize: '0.8rem', color: PALETTE.textMuted }}>
            No route access configured.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {routes.map((ra, i) => {
              const acc = ra.access && typeof ra.access === 'object' ? ra.access : {};
              const active =
                ra.is_active === true || ra.is_active === 'true' || ra.is_active === undefined;
              return (
                <Box
                  key={`${ra.route}-${i}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 1.75,
                    py: 1.1,
                    borderRadius: '5px',
                    bgcolor: active ? PALETTE.caramelLight : PALETTE.tableHeadBg,
                    border: `1px solid ${active ? PALETTE.caramelBorder : PALETTE.border}`,
                    flexWrap: 'wrap',
                    transition: 'background 0.15s',
                  }}
                >
                  {/* page label + route path */}
                  <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Inter Bold',
                        fontSize: '0.8rem',
                        color: PALETTE.textPrimary,
                      }}
                    >
                      {routeAccessLabel(ra.route)}
                    </Typography>
                    <Typography
                      sx={{ fontSize: '0.7rem', color: PALETTE.textMuted, fontFamily: 'monospace' }}
                    >
                      {ra.route}
                    </Typography>
                  </Box>

                  {/* permission dots */}
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {PERM_KEYS.map((k) => (
                      <Box
                        key={k}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.3,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.61rem',
                            color: PALETTE.textMuted,
                            fontFamily: 'Inter Bold',
                            textTransform: 'capitalize',
                          }}
                        >
                          {k}
                        </Typography>
                        <PermDot on={!!acc[k]} />
                      </Box>
                    ))}
                  </Box>

                  {/* active badge */}
                  <Chip
                    label={active ? 'Active' : 'Inactive'}
                    size='small'
                    sx={{
                      fontFamily: 'Inter Bold',
                      fontSize: '0.64rem',
                      height: 20,
                      bgcolor: active ? '#F0FDF4' : PALETTE.tableHeadBg,
                      color: active ? '#16a34a' : PALETTE.textMuted,
                      border: `1px solid ${active ? '#86efac' : PALETTE.border}`,
                      borderRadius: '5px',
                      ml: 'auto',
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* ── special conditions card ── */}
      <Paper
        variant='outlined'
        sx={{
          borderRadius: '5px',
          p: 2.25,
          bgcolor: PALETTE.cardBg,
          border: `1px solid ${PALETTE.border}`,
          boxShadow: '0 2px 8px rgba(44,26,14,0.05)',
        }}
      >
        <SectionLabel icon={StarOutlineRoundedIcon} label='Special condition access' />
        {specials.length === 0 ? (
          <Typography sx={{ fontSize: '0.8rem', color: PALETTE.textMuted }}>
            None configured.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {specials.map((s) => (
              <Chip
                key={String(s)}
                label={String(s)}
                size='small'
                sx={{
                  fontFamily: 'Inter Bold',
                  fontSize: '0.72rem',
                  height: 24,
                  bgcolor: PALETTE.caramelLight,
                  color: BRAND.blue,
                  border: `1px solid ${PALETTE.caramelBorder}`,
                  borderRadius: '5px',
                }}
              />
            ))}
          </Box>
        )}
      </Paper>
      </Box>
    </Box>
  );
}

/* ─────────────────────────────────────────────
   RouteAccessRow — used inside EditMemberModal
───────────────────────────────────────────── */
export function RouteAccessRow({ entry, label, onChange }) {
  const toggle = (key, val) => {
    if (key === 'is_active') {
      onChange({ ...entry, is_active: val });
    } else {
      onChange({ ...entry, access: { ...entry.access, [key]: val } });
    }
  };

  return (
    <Box
      sx={{
        border: `1px solid ${entry.is_active ? PALETTE.caramelBorder : PALETTE.border}`,
        borderRadius: '5px',
        overflow: 'hidden',
        transition: 'border-color 0.2s, background 0.2s',
        bgcolor: entry.is_active ? PALETTE.caramelLight : PALETTE.tableHeadBg,
      }}
    >
      {/* row header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShieldOutlinedIcon
            sx={{ fontSize: 15, color: entry.is_active ? BRAND.orange : PALETTE.textMuted }}
          />
          <Typography
            sx={{
              fontFamily: entry.is_active ? 'Inter Bold' : 'Inter Regular',
              fontSize: '0.83rem',
              color: entry.is_active ? PALETTE.textPrimary : PALETTE.textMuted,
              transition: 'all 0.2s',
            }}
          >
            {label}
          </Typography>
        </Box>
        <Switch
          checked={entry.is_active}
          onChange={(e) => toggle('is_active', e.target.checked)}
          size='small'
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND.orange },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: BRAND.orange },
          }}
        />
      </Box>

      {/* permission checkboxes */}
      <Collapse in={entry.is_active}>
        <Box
          sx={{
            display: 'flex',
            gap: 0,
            px: 2,
            pb: 1.25,
            borderTop: `1px solid ${PALETTE.caramelBorder}`,
            flexWrap: 'wrap',
          }}
        >
          {ACCESS_KEYS.map((key) => (
            <FormControlLabel
              key={key}
              label={
                <Typography
                  sx={{
                    fontFamily: 'Inter Regular',
                    fontSize: '0.75rem',
                    color: PALETTE.textSecondary,
                    textTransform: 'capitalize',
                  }}
                >
                  {key}
                </Typography>
              }
              control={
                <Checkbox
                  checked={entry.access[key]}
                  onChange={(e) => toggle(key, e.target.checked)}
                  size='small'
                  icon={ROUTE_PERM_CHECKBOX_UNCHECKED}
                  checkedIcon={ROUTE_PERM_CHECKBOX_CHECKED}
                  sx={{ p: '4px' }}
                />
              }
              sx={{ mr: 1.5, ml: 0, mt: 0.5 }}
            />
          ))}

          {/* select all */}
          <FormControlLabel
            label={
              <Typography
                sx={{ fontFamily: 'Inter Bold', fontSize: '0.75rem', color: BRAND.orange }}
              >
                All
              </Typography>
            }
            control={
              <Checkbox
                checked={ACCESS_KEYS.every((k) => entry.access[k])}
                indeterminate={
                  ACCESS_KEYS.some((k) => entry.access[k]) &&
                  !ACCESS_KEYS.every((k) => entry.access[k])
                }
                onChange={(e) => {
                  const all = e.target.checked;
                  onChange({ ...entry, access: { read: all, write: all, edit: all, delete: all } });
                }}
                size='small'
                sx={{
                  color: `${BRAND.orange}80`,
                  '&.Mui-checked': { color: BRAND.orange },
                  '&.MuiCheckbox-indeterminate': { color: BRAND.orange },
                  p: '4px',
                }}
              />
            }
            sx={{ mr: 0, ml: 'auto', mt: 0.5 }}
          />
        </Box>
      </Collapse>
    </Box>
  );
}
